import {
  REVIEW_STATUS_APPROVED,
  REVIEW_STATUS_PENDING,
  REVIEW_STATUS_REJECTED,
  type ReviewStatus,
} from '@iws/theme-schema/reviews'
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle2,
  MessageSquare,
  RefreshCw,
  Search,
  Star,
  Trash2,
  XCircle,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { useState } from 'react'

import {
  useDeleteReview,
  useReviewCounts,
  useReviews,
  useUpdateReviewStatus,
} from '@/hooks/features/reviews'
import { useDebouncedCallback } from '@/hooks/useDebounce'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination-controls'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

import { ConfirmDialog, PageHeader, StatsCards } from '@/components/atoms'

import type { Review } from '@/types/reviews'

type StatusTab = ReviewStatus | 'all'

const TABS: {
  value: StatusTab
  label: string
  count: 'pending' | 'approved' | 'rejected' | 'all'
}[] = [
  { value: REVIEW_STATUS_PENDING, label: 'Awaiting approval', count: 'pending' },
  { value: REVIEW_STATUS_APPROVED, label: 'Approved', count: 'approved' },
  { value: REVIEW_STATUS_REJECTED, label: 'Rejected', count: 'rejected' },
  { value: 'all', label: 'All', count: 'all' },
]

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })

const Stars = ({ rating }: { rating: number }) => (
  <div className="flex text-warning" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`size-4 ${i < rating ? 'fill-current' : 'text-faint'}`} />
    ))}
  </div>
)

const StatusBadge = ({ status }: { status: string }) => {
  if (status === REVIEW_STATUS_APPROVED) return <Badge tone="success">Approved</Badge>
  if (status === REVIEW_STATUS_REJECTED) return <Badge tone="error">Rejected</Badge>
  return <Badge tone="warning">Awaiting approval</Badge>
}

export const ReviewsDashboard = () => {
  const [status, setStatus] = useState<StatusTab>(REVIEW_STATUS_PENDING)
  const [rating, setRating] = useState<number | 'all'>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [length, setLength] = useState(10)
  const [toDelete, setToDelete] = useState<Review | null>(null)

  const { data, isLoading, isError, error, refetch, isFetching } = useReviews({
    page,
    length,
    search,
    status,
    rating,
  })
  const { data: counts } = useReviewCounts()
  const updateStatus = useUpdateReviewStatus()
  const deleteReview = useDeleteReview()

  const applySearch = useDebouncedCallback((value: string) => {
    setSearch(value.trim())
    setPage(1)
  }, 400)

  const changeStatus = (review: Review, next: ReviewStatus) =>
    updateStatus.mutate(
      { id: review._id, status: next },
      {
        onSuccess: () =>
          toast.success(
            next === REVIEW_STATUS_APPROVED
              ? 'Review approved — it is now on your store'
              : 'Review rejected'
          ),
        onError: (e) => toast.error(e.message),
      }
    )

  const confirmDelete = () => {
    if (!toDelete) return
    deleteReview.mutate(toDelete._id, {
      onSuccess: () => {
        toast.success('Review deleted')
        setToDelete(null)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const reviews = data?.result ?? []
  const busyId = updateStatus.isPending ? updateStatus.variables?.id : undefined

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reviews"
        description="Reviews from customers who received the product. A review appears on your store and counts towards the product's rating only after you approve it."
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-border-subtle p-2 lg:flex-row lg:items-center lg:justify-between">
          <StatsCards
            variant="tag"
            stats={TABS.map((t) => ({
              title: t.label,
              value: counts ? counts[t.count] : '',
              active: status === t.value,
              onClick: () => {
                setStatus(t.value)
                setPage(1)
              },
            }))}
          />
          <div className="flex items-center gap-2">
            <Select
              value={String(rating)}
              onValueChange={(v) => {
                setRating(v === 'all' ? 'all' : Number(v))
                setPage(1)
              }}
            >
              <SelectTrigger className="w-32 shrink-0" aria-label="Filter by rating">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ratings</SelectItem>
                {[5, 4, 3, 2, 1].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} star{n > 1 ? 's' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search reviews"
                placeholder="Search title or text"
                className="pl-8"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  applySearch(e.target.value)
                }}
              />
            </div>
            <Button variant="outline" size="icon" aria-label="Refresh" onClick={() => refetch()}>
              <RefreshCw className={isFetching ? 'animate-spin' : undefined} />
            </Button>
          </div>
        </div>

        <div>
          {isLoading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Could not load reviews</AlertTitle>
                <AlertDescription className="flex flex-wrap items-center gap-3">
                  <span>{error.message}</span>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : reviews.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <span
                className="grid size-16 place-items-center rounded-full bg-subdued"
                aria-hidden="true"
              >
                <MessageSquare className="size-6 text-faint" />
              </span>
              <p className="text-sm text-muted-foreground">
                {search || rating !== 'all'
                  ? 'No reviews match these filters.'
                  : status === REVIEW_STATUS_PENDING
                    ? 'No reviews are waiting for approval.'
                    : 'No reviews here yet.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {reviews.map((review) => (
                <li
                  key={review._id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start"
                >
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Stars rating={review.rating} />
                      <StatusBadge status={review.status} />
                      {review.is_verified && (
                        <span className="flex items-center gap-1 text-xs text-success-dark">
                          <BadgeCheck className="size-4" /> Verified buyer
                        </span>
                      )}
                    </div>
                    {review.title && <p className="text-sm font-medium text-ink">{review.title}</p>}
                    {review.review && (
                      <p className="whitespace-pre-line text-sm text-text-2">{review.review}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {review.product ? (
                        <Link
                          to={`/products/${review.product._id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {review.product.name}
                        </Link>
                      ) : (
                        'Deleted product'
                      )}
                      {' · '}
                      {review.customer ? (
                        <Link to={`/customers/${review.customer._id}`} className="hover:underline">
                          {review.customer.name ||
                            review.customer.phone ||
                            review.customer.email ||
                            'Customer'}
                        </Link>
                      ) : (
                        'Deleted customer'
                      )}
                      {' · '}
                      {formatDate(review.created_at)}
                      {review.helpful_count ? ` · ${review.helpful_count} found it helpful` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {review.status !== REVIEW_STATUS_APPROVED && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === review._id}
                        onClick={() => changeStatus(review, REVIEW_STATUS_APPROVED)}
                      >
                        <CheckCircle2 className="text-success-dark" /> Approve
                      </Button>
                    )}
                    {review.status !== REVIEW_STATUS_REJECTED && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === review._id}
                        onClick={() => changeStatus(review, REVIEW_STATUS_REJECTED)}
                      >
                        <XCircle className="text-error-dark" /> Reject
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete review"
                      onClick={() => setToDelete(review)}
                    >
                      <Trash2 className="text-error" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {data && !isError && (
            <div className="flex flex-col gap-2 px-4 pb-3">
              <Pagination.Buttons
                currentPage={page}
                totalItems={data.count}
                pageSize={length}
                onPageChange={setPage}
              />
              <Pagination.PageSizeDropdown
                pageSize={length}
                onPageSizeChange={(size) => {
                  setLength(size)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </Card>

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete this review?"
        description="It is removed from your store and from the product's rating. The customer can write a new one."
        confirmText="Delete"
        variant="destructive"
        loading={deleteReview.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
